/**
 * User management constants
 *
 * Extracted from useUserManagement.js for reusability and testability.
 */

import { t } from '../../i18n';

export const USER_MODES = {
  create: 'create',
  edit: 'edit',
};

/**
 * Dropdown option lists.
 *
 * These are functions, not constants: the `label` is display text and is
 * translated, so it has to resolve at call time. The `value` is canonical and
 * is what gets sent to the API — note `{ label: 'Admin', value: 'Analyst' }`,
 * where the two deliberately differ. Translating a value would silently break
 * role assignment, so only labels come from the catalog.
 *
 * Call sites seed React state from these, so a language change is reflected the
 * next time the screen mounts rather than instantly.
 */

const role = (value) => ({ label: t(`admin:roles.${value}`), value });

export function getDefaultRoles() {
  return [role('Reporter'), role('Analyst')];
}

export function getOptionalRoles() {
  return [{ label: t('admin:roles.keepCurrent'), value: null }, role('Reporter'), role('Analyst')];
}

// Additional admin roles - only account owners can assign these
export function getAdminRoles() {
  return [role('UserAdmin'), role('OrgAdmin'), role('Moneta')];
}

export function getStatusOptions() {
  return ['Active', 'Pending', 'Inactive'].map((value) => ({
    label: t(`admin:userStatus.${value}`),
    value,
  }));
}

export function getEscalationTierOptions() {
  return [1, 2, 3].map((value) => ({ label: t(`admin:escalationTier.${value}`), value }));
}

export const VALID_ROLES = {
  reporter: 'Reporter',
  analyst: 'Analyst',
  admin: 'Analyst', // alias for analyst - users commonly use 'admin' in CSV
  useradmin: 'UserAdmin',
  orgadmin: 'OrgAdmin',
  moneta: 'Moneta',
};

/**
 * Pending status threshold in milliseconds (7 days)
 */
export const PENDING_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;
